import axios from 'axios'
import { createAuthedClient } from '../../lib/createAuthedClient'

export const customerApi = createAuthedClient({
  baseURL: '/api/customer',
  storageKey: 'customer-auth-storage',
  refreshUrl: '/api/auth/customer/refresh',
  loginPath: '/login',
})

export const customerAuthApi = axios.create({
  baseURL: '/api/auth/customer',
  headers: { 'Content-Type': 'application/json' },
})

/**
 * Hebt die tatsaechliche Backend-Fehlermeldung in `error.message` an, damit alle
 * Pages, die `e.message` anzeigen, statt des generischen Axios-Strings
 * ("Request failed with status code 409") die echte Ursache sehen.
 * Der Backend-Code wird zusaetzlich auf `error.backendCode` gelegt, damit UIs
 * typisiert reagieren koennen (z.B. `CUSTOMER_NO_SAP_LINK`, `TANSS_…`).
 * Pflicht laut CLAUDE.md: alle Debug-Infos muessen im Frontend sichtbar sein.
 */
function enrichAxiosError(err: unknown): unknown {
  const e = err as {
    message?: string
    response?: { status?: number; data?: unknown }
    code?: string
    backendCode?: string
  }
  const status = e?.response?.status
  let data = e?.response?.data

  // Bei `responseType: 'arraybuffer'` (PDF-Download etc.) kommt auch der
  // Error-Body als ArrayBuffer zurueck — ohne Decode liest man dann nur
  // "[object ArrayBuffer]" und sieht den eigentlichen Backend-JSON-Fehler nicht.
  if (data instanceof ArrayBuffer) {
    try {
      const text = new TextDecoder().decode(data)
      data = text.length > 0 ? JSON.parse(text) : text
    } catch { /* roher Text bleibt im else-if-Zweig unten */ }
  }

  let backendMsg: string | undefined
  let backendCode: string | undefined
  if (data && typeof data === 'object') {
    const body = data as Record<string, unknown>
    if (typeof body.error === 'string') backendMsg = body.error
    else if (typeof body.message === 'string') backendMsg = body.message
    if (typeof body.code === 'string') backendCode = body.code
  } else if (typeof data === 'string' && data.length > 0 && data.length < 500) {
    backendMsg = data
  }
  if (backendMsg) {
    const codeSuffix = backendCode ? ` [${backendCode}]` : ''
    const statusSuffix = status ? ` (HTTP ${status})` : ''
    e.message = `${backendMsg}${codeSuffix}${statusSuffix}`
  } else if (!e?.response && e?.code === 'ERR_NETWORK') {
    e.message = 'Server nicht erreichbar (Netzwerkfehler)'
  }
  if (backendCode) e.backendCode = backendCode
  return err
}

// Laeuft nach den Interceptors aus createAuthedClient – erhaelt also das
// finale Error-Objekt (auch nach fehlgeschlagenem Refresh) und reichert es an.
customerApi.interceptors.response.use(
  (r) => r,
  (error) => Promise.reject(enrichAxiosError(error)),
)

customerAuthApi.interceptors.response.use(
  (r) => r,
  (error) => Promise.reject(enrichAxiosError(error)),
)
